import React, {memo, useCallback, useMemo} from 'react';
import './Switch.scss'

/**
 * Emulates an enum of colors that the Switch can use.
 * Assumes that there are CSS classes defined for these colors.
 */
export class SwitchColor {
    static GREEN = new SwitchColor('green');
    static ORANGE = new SwitchColor('orange');
    static BLUE = new SwitchColor('blue');
    static #validColors = [SwitchColor.GREEN, SwitchColor.ORANGE, SwitchColor.BLUE];

    constructor(name) {
        this.name = name;
    }

    /**
     * A valid color is a color that's an instance of {@link SwitchColor}
     * and is included in the list of valid colors.
     * @param color
     * @returns {boolean}
     */
    static isValid(color) {
        return (color instanceof SwitchColor && this.#validColors.includes(color));
    }

    /**
     * Determines whether two colors are identical by their name
     * @param color1 First color in the comparison
     * @param color2 Second color in the comparison
     * @returns {boolean}
     */
    static areEqual(color1, color2) {
        return color1?.name === color2?.name;
    }

    toString() {
        return this.name || super.toString();
    }
}

const Switch = memo(({label, color, isChecked, changeHandler, classNames, dataTags}) => {
        color = SwitchColor.isValid(color) ? color : SwitchColor.GREEN;
        isChecked = useMemo(() => isChecked, [isChecked]);
        changeHandler = useCallback(changeHandler, []);
        classNames = classNames?.join(' ');
        // TODO: Validate dataTags format object with key(s): 'data-<tag-name>' and value(s): <non-empty string>

        const stopPropagation = (e) => {
            // Stop input and slider from bubbling any further
            e.stopPropagation();
        }

        const renderSwitch = () => (
            <label className={`switch ${(!label && classNames) || ''}`} onClick={stopPropagation}>
                <input type="checkbox" checked={isChecked} onChange={changeHandler} {...dataTags}/>
                <i className={`slider ${color}`}/>
            </label>
        );

        const renderLabelledSwitch = () => (
            <div className={`labelled-switch ${classNames || ''}`}>
                <span className="label">{label}</span>
                {renderSwitch()}
            </div>
        );

        return (label ? renderLabelledSwitch() : renderSwitch());
    }, (prevProps, nextProps) =>
        prevProps.isChecked === nextProps.isChecked && prevProps.label === nextProps.label
        && SwitchColor.areEqual(prevProps.color, nextProps.color)
);

export default Switch;